import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import { marketplaceApi } from '../../api/marketplace';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface IntegrationConfig {
  whatsapp: {
    enabled: boolean;
    apiKey: string;
    phoneNumberId: string;
    businessAccountId: string;
    webhookSecret: string;
    webhookUrl: string;
    rateLimit: number;
  };
  facebook: {
    enabled: boolean;
    appId: string;
    appSecret: string;
    pageId: string;
    accessToken: string;
  };
  instagram: {
    enabled: boolean;
    businessAccountId: string;
    accessToken: string;
  };
  telegram: {
    enabled: boolean;
    botToken: string;
    channelId: string;
  };
  queue: {
    enabled: boolean;
    retryAttempts: number;
    retryDelay: number;
    maxConcurrent: number;
  };
}

interface QueueStats {
  pending: number;
  processing: number;
  sent: number;
  failed: number;
  retry: number;
}

export default function IntegrationSettings() {
  const { user } = useAuth();
  const [config, setConfig] = useState<IntegrationConfig | null>(null);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testMessage, setTestMessage] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('whatsapp');

  useEffect(() => {
    loadConfig();
    loadQueueStats();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await marketplaceApi.getIntegrationConfig();
      setConfig(response);
    } catch (error) {
      console.error('Failed to load integration config:', error);
      toast.error('Failed to load integration configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadQueueStats = async () => {
    try {
      const response = await marketplaceApi.getQueueStats();
      setQueueStats(response);
    } catch (error) {
      console.error('Failed to load queue stats:', error);
    }
  };

  const handleConfigChange = (platform: string, field: string, value: any) => {
    if (!config) return;

    setConfig({
      ...config,
      [platform]: {
        ...config[platform as keyof IntegrationConfig],
        [field]: value,
      },
    });
  };

  const saveConfig = async () => {
    if (!config) return;

    try {
      setSaving(true);
      // This would be implemented in the API to save the configuration
      await marketplaceApi.updateIntegrationConfig(config);
      toast.success('Integration configuration saved successfully');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save integration configuration');
    } finally {
      setSaving(false);
    }
  };

  const testIntegration = async () => {
    if (!testMessage || !testRecipient) {
      toast.error('Please enter both message and recipient');
      return;
    }

    try {
      const response = await marketplaceApi.testIntegration({
        platform: selectedPlatform as any,
        recipientId: testRecipient,
        message: testMessage,
      });

      if (response.success) {
        toast.success(`Test message sent successfully to ${selectedPlatform}`);
        setTestMessage('');
        setTestRecipient('');
      }
    } catch (error) {
      console.error('Failed to send test message:', error);
      toast.error('Failed to send test message');
    }
  };

  const toggleSecretVisibility = (platform: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [platform]: !prev[platform],
    }));
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return <ChatBubbleLeftRightIcon className="h-6 w-6 text-green-500" />;
      case 'facebook':
        return <PhotoIcon className="h-6 w-6 text-cyan-600" />;
      case 'instagram':
        return <PhotoIcon className="h-6 w-6 text-pink-500" />;
      case 'telegram':
        return <DevicePhoneMobileIcon className="h-6 w-6 text-blue-500" />;
      default:
        return <CogIcon className="h-6 w-6 text-slate-500" />;
    }
  };

  const getPlatformName = (platform: string) => {
    switch (platform) {
      case 'whatsapp':
        return 'WhatsApp Business API';
      case 'facebook':
        return 'Facebook';
      case 'instagram':
        return 'Instagram';
      case 'telegram':
        return 'Telegram Bot';
      default:
        return platform;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-900 mb-2">Configuration Not Found</h3>
        <p className="text-slate-600">Unable to load integration configuration.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h2 className="text-2xl font-bold text-slate-900">External Integrations</h2>
        <p className="text-slate-600 mt-1">
          Configure and manage external platform integrations for your marketplace.
        </p>
      </div>

      {/* Queue Statistics */}
      {queueStats && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-slate-900 mb-4">Message Queue Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{queueStats.pending}</div>
              <div className="text-sm text-slate-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-cyan-600">{queueStats.processing}</div>
              <div className="text-sm text-slate-600">Processing</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{queueStats.sent}</div>
              <div className="text-sm text-slate-600">Sent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{queueStats.failed}</div>
              <div className="text-sm text-slate-600">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{queueStats.retry}</div>
              <div className="text-sm text-slate-600">Retry</div>
            </div>
          </div>
        </div>
      )}

      {/* Platform Configurations */}
      <div className="space-y-6">
        {Object.entries(config).map(([platform, platformConfig]) => {
          if (platform === 'queue') return null; // Skip queue config for now
          
          return (
            <div key={platform} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getPlatformIcon(platform)}
                    <div>
                      <h3 className="text-lg font-medium text-slate-900">
                        {getPlatformName(platform)}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <div className={`flex items-center space-x-1 ${
                          platformConfig.enabled ? 'text-green-600' : 'text-slate-400'
                        }`}>
                          {platformConfig.enabled ? (
                            <CheckCircleIcon className="h-4 w-4" />
                          ) : (
                            <XCircleIcon className="h-4 w-4" />
                          )}
                          <span className="text-sm">
                            {platformConfig.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={platformConfig.enabled}
                      onChange={(e) => handleConfigChange(platform, 'enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                  </label>
                </div>
              </div>

              {platformConfig.enabled && (
                <div className="px-6 py-4 space-y-4">
                  {platform === 'whatsapp' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          API Key
                        </label>
                        <div className="relative">
                          <input
                            type={showSecrets[platform] ? 'text' : 'password'}
                            value={platformConfig.apiKey}
                            onChange={(e) => handleConfigChange(platform, 'apiKey', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                            placeholder="Enter WhatsApp Business API Key"
                          />
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(platform)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showSecrets[platform] ? (
                              <EyeSlashIcon className="h-5 w-5 text-slate-400" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Phone Number ID
                          </label>
                          <input
                            type="text"
                            value={platformConfig.phoneNumberId}
                            onChange={(e) => handleConfigChange(platform, 'phoneNumberId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter Phone Number ID"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Business Account ID
                          </label>
                          <input
                            type="text"
                            value={platformConfig.businessAccountId}
                            onChange={(e) => handleConfigChange(platform, 'businessAccountId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter Business Account ID"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Webhook URL
                        </label>
                        <input
                          type="text"
                          value={platformConfig.webhookUrl}
                          onChange={(e) => handleConfigChange(platform, 'webhookUrl', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="https://your-domain.com/api/marketplace/webhooks/whatsapp"
                        />
                      </div>
                    </>
                  )}

                  {platform === 'facebook' && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            App ID
                          </label>
                          <input
                            type="text"
                            value={platformConfig.appId}
                            onChange={(e) => handleConfigChange(platform, 'appId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter Facebook App ID"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Page ID
                          </label>
                          <input
                            type="text"
                            value={platformConfig.pageId}
                            onChange={(e) => handleConfigChange(platform, 'pageId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter Facebook Page ID"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {platform === 'telegram' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Bot Token
                        </label>
                        <div className="relative">
                          <input
                            type={showSecrets[platform] ? 'text' : 'password'}
                            value={platformConfig.botToken}
                            onChange={(e) => handleConfigChange(platform, 'botToken', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 pr-10"
                            placeholder="Enter Telegram Bot Token"
                          />
                          <button
                            type="button"
                            onClick={() => toggleSecretVisibility(platform)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          >
                            {showSecrets[platform] ? (
                              <EyeSlashIcon className="h-5 w-5 text-slate-400" />
                            ) : (
                              <EyeIcon className="h-5 w-5 text-slate-400" />
                            )}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Channel ID
                        </label>
                        <input
                          type="text"
                          value={platformConfig.channelId}
                          onChange={(e) => handleConfigChange(platform, 'channelId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter Telegram Channel ID"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Test Integration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Test Integration</h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Platform
              </label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="telegram">Telegram</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Recipient ID
              </label>
              <input
                type="text"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter recipient ID"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message
              </label>
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter test message"
              />
            </div>
          </div>
          <button
            onClick={testIntegration}
            disabled={!testMessage || !testRecipient}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
            Send Test Message
          </button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveConfig}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </button>
      </div>
    </div>
  );
}
